import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * `static`  — never animates: motion is reduced, or the element was already on
 *             screen when the page became interactive.
 * `waiting` — armed and below the fold, so it may start hidden.
 * `shown`   — has entered the viewport; it never goes back.
 */
export type InViewState = 'static' | 'waiting' | 'shown';

type Options = {
  rootMargin?: string;
  /**
   * When true (the default) an element already on screen at mount stays
   * `static`. Content in the prerendered HTML is therefore never hidden and
   * then faded in, which would read as a flash on a fast connection (§52).
   * Counters pass false: they should still run when they load in view.
   */
  skipAboveFold?: boolean;
};

/**
 * Watches one element enter the viewport, once. The observer disconnects on
 * the first intersection, so nothing stays subscribed to scroll after it has
 * done its job.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>({
  rootMargin = '0px 0px -10% 0px',
  skipAboveFold = true,
}: Options = {}): { ref: RefObject<T | null>; state: InViewState } {
  const ref = useRef<T>(null);
  const [state, setState] = useState<InViewState>('static');

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const aboveFold = el.getBoundingClientRect().top < window.innerHeight;
    if (aboveFold && skipAboveFold) return;

    setState(aboveFold ? 'shown' : 'waiting');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setState('shown');
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, skipAboveFold]);

  return { ref, state };
}
